import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  Button,
  Icon,
  Input,
  Select,
  Table,
  TitleSecondary,
} from '../../components';
import { TableCell, TableRow } from '../../components/tableSimple/tableSimple';

import { actions } from '../../state';

import {
  formatDate,
  formatMoney,
  mapMembershipOrderStatus,
  mapMembershipOrderStatusColor,
} from '../../utils';
import { config } from '../../api/config';

import styles from './clients.module.scss';

const DEACTIVATED_MEMBERSHIP_STATUSES = [553, 554];

const DiscountPlans = ({
  dispatch,
  client_memberships,
  onSubmit,
  memberships: { memberships, loading: membershipsLoading },
  clientLoading,
}) => {
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [values, setValues] = useState({
    membership_id: 0,
    valid_from: '',
    valid_until: '',
  });
  const handleInputChange = ({ target: { name, value } }) => {
    setValues({ ...values, [name]: value });
  };

  const fetchMemberships = useCallback(() => {
    dispatch(
      actions.memberships.getMembershipsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'title_lt',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  const clearMemberships = useCallback(() => {
    dispatch(actions.memberships.clearMembershipsAction());
  }, [dispatch]);

  useEffect(() => () => clearMemberships(), [clearMemberships]);

  const membershipOptions = useMemo(
    () => [
      { name: 'Pasirinkite planą', value: '' },
      ...memberships.map(m => ({
        name: m.title_lt,
        value: m.id,
      })),
    ],
    [memberships]
  );

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const sortedMemberships = useMemo(
    () =>
      [...client_memberships].sort((a, b) => {
        const first = new Date(
          b.valid_from || b.created_at || b.valid_until || 0
        ).getTime();
        const second = new Date(
          a.valid_from || a.created_at || a.valid_until || 0
        ).getTime();
        return first - second;
      }),
    [client_memberships]
  );

  const visibleMemberships = showAllHistory
    ? sortedMemberships
    : sortedMemberships.slice(0, 3);

  const hiddenMembershipsCount =
    sortedMemberships.length - visibleMemberships.length;

  const loading = membershipsLoading || clientLoading;

  const handleDeactivateMembership = id => {
    if (window.confirm('Ar tikrai norite deaktyvuoti pasirinktą planą?')) {
      dispatch(actions.clients.deactivateClientsMembershipOrderAction(id));
    }
  };

  const tableHeader = [
    '#',
    'Statusas',
    'Planas',
    'Galioja nuo - iki',
    'Kaina',
    '',
  ];

  const renderTable = () =>
    visibleMemberships.map(
      ({
        id,
        status,
        membership_title_lt,
        title_lt,
        valid_from,
        valid_until,
        price,
      }) => {
        const planName = membership_title_lt || title_lt || '-';
        const canDeactivate = !DEACTIVATED_MEMBERSHIP_STATUSES.includes(status);

        return (
          <TableRow key={id}>
            <TableCell>{id}</TableCell>
            <TableCell>
              <span style={{ color: mapMembershipOrderStatusColor(status) }}>
                {mapMembershipOrderStatus(status)}
              </span>
            </TableCell>
            <TableCell>{planName}</TableCell>
            <TableCell>
              {formatDate(valid_from)}-{formatDate(valid_until)}
            </TableCell>
            <TableCell>{formatMoney(price || 0)}</TableCell>
            <TableCell>
              {canDeactivate ? (
                <button
                  type="button"
                  className={styles.membershipDeactivateButton}
                  onClick={() => handleDeactivateMembership(id)}
                  disabled={loading}
                  title="Deaktyvuoti planą"
                  aria-label="Deaktyvuoti planą"
                >
                  <Icon
                    name="remove-red"
                    classes={{ icon: styles.membershipDeactivateIcon }}
                  />
                </button>
              ) : null}
            </TableCell>
          </TableRow>
        );
      }
    );

  const isActionDisabled =
    !values.valid_from || !values.valid_until || !values.membership_id;

  return (
    <div className={styles.discountPlansContainer}>
      <TitleSecondary>Nuolaidų planai</TitleSecondary>
      <div className={styles.activateDiscountContainer}>
        <Select
          name="membership_id"
          options={membershipOptions}
          selectedValue={values.membership_id}
          disabled={loading}
          onChange={handleInputChange}
        />
        <div className={styles.selectValidDates}>
          <Input
            name="valid_from"
            type="date"
            max="2999-12-31"
            placeholder={config.DEFAULTS.dateFormat}
            onChange={handleInputChange}
            value={values.valid_from}
            disabled={loading}
            required
          />
          <Input
            name="valid_until"
            type="date"
            max="2999-12-31"
            placeholder={config.DEFAULTS.dateFormat}
            onChange={handleInputChange}
            value={values.valid_until}
            disabled={loading}
            required
          />
        </div>
        <Button
          color="primary"
          disabled={isActionDisabled}
          onClick={() => onSubmit(values)}
        >
          Aktyvuoti planą
        </Button>
      </div>
      <div className={styles.discountPlansHistoryContainer}>
        <h4 className={styles.discountPlansHistoryTitle}>Istorija</h4>
        <Table
          header={tableHeader}
          isEmpty={visibleMemberships.length === 0}
          filter={false}
          loading={loading}
          footer={null}
        >
          {renderTable()}
        </Table>
        {sortedMemberships.length > 3 && (
          <button
            type="button"
            className={styles.showMoreButton}
            onClick={() => setShowAllHistory(prev => !prev)}
          >
            {showAllHistory
              ? 'Rodyti mažiau'
              : `Rodyti daugiau (${hiddenMembershipsCount})`}
            <Icon
              name="chevron-down"
              classes={{
                icon: `${styles.showMoreButtonIcon} ${
                  showAllHistory ? styles.showMoreButtonIconExpanded : ''
                }`,
              }}
            />
          </button>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  memberships: state.memberships,
  clientLoading: state.clients.loading,
});

DiscountPlans.propTypes = {
  dispatch: PropTypes.func.isRequired,
  memberships: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    memberships: PropTypes.array.isRequired,
  }).isRequired,
  clientLoading: PropTypes.bool.isRequired,
  client_memberships: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(DiscountPlans);
